import React, { useEffect, useRef } from "react";
import {
  useApplyVideoObjectFit,
  useAudioVideo,
  useLocalVideo,
  useMeetingManager,
} from "amazon-chime-sdk-component-library-react";
import { VideoMeetingTile } from "./VideoMeetingTile";
import { usePlatformStore, useVideoMeetingMembers } from "@platform/store";

interface Props {
  id?: string;
  isWithContentShare?: boolean;
  tileIndex?: number;
  totalTiles?: number;
  nameplate?: string;
  className?: string;
}

export const VideoMeetingTileLocalVideoContainer: React.FC<
  React.PropsWithChildren<Props>
> = ({
  tileIndex,
  totalTiles,
  nameplate,
  className,
  isWithContentShare,
  ...rest
}) => {
  const { tileId, isVideoEnabled } = useLocalVideo();

  const audioVideo = useAudioVideo();

  const meetingManager = useMeetingManager();

  const videoEl = useRef<HTMLVideoElement>(null);

  useApplyVideoObjectFit(videoEl);

  const user = usePlatformStore((s) => s.user);
  const connectionQuality = usePlatformStore((s) => s.connectionQuality);
  const { roomMembersData } = useVideoMeetingMembers();
  const roomMemberData = user ? (roomMembersData[user.id] ?? null) : null;

  // Get local attendee ID for active speaker detection
  const localAttendeeId =
    meetingManager.meetingSession?.configuration.credentials?.attendeeId ||
    null;

  // Track previous tile ID to unbind old + bind new in the same effect run
  const prevTileIdRef = useRef<number>(0);

  useEffect(() => {
    if (!audioVideo || !tileId || !videoEl.current || !isVideoEnabled) {
      return;
    }

    // Unbind previous tile if it changed (avoids gap frames)
    if (prevTileIdRef.current > 0 && prevTileIdRef.current !== tileId) {
      const prevTile = audioVideo.getVideoTile(prevTileIdRef.current);
      if (prevTile) {
        audioVideo.unbindVideoElement(prevTileIdRef.current);
      }
    }

    audioVideo.bindVideoElement(tileId, videoEl.current);
    prevTileIdRef.current = tileId;

    return () => {
      const tile = audioVideo.getVideoTile(tileId);
      if (tile) {
        audioVideo.unbindVideoElement(tileId);
      }
      prevTileIdRef.current = 0;
    };
  }, [audioVideo, tileId, isVideoEnabled]);

  return (
    <VideoMeetingTile
      className={className}
      nameplate={nameplate}
      isWithContentShare={isWithContentShare}
      ref={videoEl}
      isEnabled={isVideoEnabled}
      tileIndex={tileIndex}
      totalTiles={totalTiles}
      attendeeId={localAttendeeId || undefined}
      roomMemberData={roomMemberData}
      connectionQuality={connectionQuality}
      {...rest}
    />
  );
};

export default VideoMeetingTileLocalVideoContainer;
